// Whale swim animation controller.
// Manages the Three.js AnimationMixer and provides an XR8 pipeline module
// that advances it every frame with adaptive slowdown for poor frame pacing.

import * as THREE from 'three';
import { getWhaleObject, getWhaleAnimationClips } from './whaleLoader';
import { logger } from '../utils/logger';

// Maximum delta passed to mixer.update() — prevents large animation jumps on
// slow or skipped frames (e.g. app backgrounding briefly).
const MAX_DELTA_SEC = 0.1;

// Frame duration considered "struggling" (~20fps). Below this rate the
// timeScale is proportionally reduced so the animation slows gracefully.
const SLOW_FRAME_SEC = 1 / 20;

let mixer: THREE.AnimationMixer | null = null;
const clock = new THREE.Clock(false);

function normalizeClipStart(clip: THREE.AnimationClip): THREE.AnimationClip {
  const firstKeyTime = clip.tracks.reduce(
    (min, track) => Math.min(min, track.times[0] ?? min),
    Infinity,
  );
  if (!Number.isFinite(firstKeyTime) || firstKeyTime <= 0) return clip;

  const normalized = clip.clone();
  normalized.tracks.forEach((track) => {
    track.shift(-firstKeyTime);
  });
  normalized.resetDuration();
  logger.log(
    `Normalized clip start: "${clip.name}" shifted by ${firstKeyTime.toFixed(3)}s;`,
    `duration ${clip.duration.toFixed(3)}s → ${normalized.duration.toFixed(3)}s`,
  );
  return normalized;
}

/**
 * Creates the AnimationMixer and starts the first clip looping from the
 * beginning. Safe to call only once — subsequent calls are no-ops.
 *
 * The mixer is always created on the top-level root group, not on a
 * sub-object such as the armature. Three.js resolves animation track targets
 * by searching the mixer root's *descendants*, so using a sub-object as root
 * prevents tracks that target that sub-object itself from ever resolving.
 * For example, a Cinema 4D FBX whose clip is named "Joints|…" will include
 * tracks that animate the "Joints" group directly; those tracks silently fail
 * if the mixer root IS "Joints" because "Joints" is not its own descendant.
 * With the root group as the mixer root, every named object in the hierarchy
 * — "Joints", "Joint" (root bone), "Spine6", etc. — is reachable.
 */
export function startWhaleAnimation(): void {
  if (mixer) return;

  const whale = getWhaleObject();
  const clips = getWhaleAnimationClips();

  if (!whale) {
    logger.warn('startWhaleAnimation: whale not loaded');
    return;
  }
  if (clips.length === 0) {
    logger.warn('startWhaleAnimation: no animation clips found in whale asset');
    return;
  }

  logger.log(`Found ${clips.length} animation clip(s):`);
  clips.forEach((c, i) => {
    logger.log(
      `  [${i}] "${c.name}" — ${c.tracks.length} tracks, ${c.duration.toFixed(2)}s`,
      c.tracks.length > 0 ? `(e.g. "${c.tracks[0].name}")` : '',
    );
  });

  // Pick the clip with the most tracks — most likely the actual swim animation.
  // clips[0] may be an empty pose or misnamed take depending on FBX exporter quirks.
  const clip = normalizeClipStart([...clips].sort((a, b) => b.tracks.length - a.tracks.length)[0]);
  logger.log(`Selected clip: "${clip.name}" (${clip.tracks.length} tracks)`);

  // Use the root group so all track targets — including the armature group
  // object itself — are reachable as descendants.
  mixer = new THREE.AnimationMixer(whale);
  const action = mixer.clipAction(clip);
  action.setLoop(THREE.LoopRepeat, Infinity);
  // Cyclic swim clips should preserve boundary velocity. Three's default
  // zero-slope easing can make the loop seam look like a brief pause.
  action.zeroSlopeAtStart = false;
  action.zeroSlopeAtEnd = false;
  action.reset().play();

  clock.start();
  logger.log('Whale animation started — isRunning:', action.isRunning(), '| timeScale:', mixer.timeScale);
}

/**
 * Returns an XR8 pipeline module that advances the mixer each AR frame.
 * Applies adaptive slowdown: if the raw frame delta exceeds the slow-frame
 * threshold, timeScale is reduced proportionally so the animation slows
 * rather than jumping. The delta itself is always capped at MAX_DELTA_SEC.
 */
export function whaleAnimationPipelineModule(): XR8PipelineModule {
  return {
    name: 'whale-animation',
    onUpdate: () => {
      if (!mixer) return;

      const raw = clock.getDelta();
      const delta = Math.min(raw, MAX_DELTA_SEC);

      // Slow playback proportionally when frame pacing degrades.
      mixer.timeScale = raw > SLOW_FRAME_SEC ? SLOW_FRAME_SEC / raw : 1;

      mixer.update(delta);
    },
  };
}
